/**
 * Bridges the single sign-on session to the app's existing auth storage.
 *
 * The access token is kept under the same storage key the HTTP client already
 * reads, and the local account record is hydrated from the API (`GET /users/me`)
 * so every existing consumer (identity, role, plan-module gating) keeps working
 * unchanged — the only thing that changed is where the token comes from.
 */

import { AUTH_CONFIG } from '@/config/constants';
import { storeAuthData, storePlanModules, clearAuthData } from '@/utils/auth';
import { httpClient } from '@/utils/httpClient';
import { resolvePlanModules } from '@/utils/planModules';
import type { ApiEnvelope, ApiUser } from '@/types/api';

/** Persist the current access token where the HTTP client expects it. */
export function syncToken(token: string): void {
  localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
}

/**
 * Hydrate the local session from a verified SSO access token: store the token,
 * fetch the account record, and load its plan modules for feature gating.
 * Throws if the API cannot resolve the account (no local user linked yet).
 */
export async function hydrateSession(token: string): Promise<void> {
  // The token must be in place before the profile call so it is sent as bearer.
  localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);

  const response = await httpClient.get<ApiEnvelope<ApiUser>>('/users/me');
  const user = response.data?.data;
  if (!user) {
    clearAuthData();
    throw new Error('Account profile unavailable');
  }

  storeAuthData(token, user);
  const modules = await resolvePlanModules(user);
  storePlanModules(modules);
}
